import { useAtomValue } from "jotai";
import { useToken } from "../../server/auth/use-token";
import { brandingImageTypeAtom } from "../brand-kit/store";
import { useClaims } from "../generic/hooks/use-claims";
import { brandInfoToCustomizations } from "./brand-info-customizations/brand-info-customizations";
import { createDesignHuddleProject, getDesignHuddlePreviewUrl } from "./design-huddle-sdk";
import { useDesignHuddleCallback } from "./use-design-huddle-callback";
import { copyImage, getPageNumber, imageURL } from "./utils";

import type { BrandInfoSelected, TemplateInfo } from "../brand-kit/types";

export function useCreateDesignHuddleProject(): (
  template_id?: string,
  settings?: BrandInfoSelected,
  templateInfo?: TemplateInfo,
) => Promise<{ project_id: string; preview_url: string } | null> {
  const user = useClaims();
  const getToken = useToken();

  const image_type = useAtomValue(brandingImageTypeAtom);

  return useDesignHuddleCallback(
    [],
    async (
      template_code?: string,
      settings?: BrandInfoSelected,
      templateInfo?: TemplateInfo,
    ): Promise<{ project_id: string; preview_url: string } | null> => {
      if (!user) return null;

      const customizations = brandInfoToCustomizations({
        brandInfoSelected: settings,
        templateInfo,
      });
      const page_number = templateInfo?.page_number ?? getPageNumber(settings);

      let project_id: string;
      try {
        project_id = await createDesignHuddleProject({
          media_type: "digital",
          dimensions: template_code
            ? undefined
            : {
                width: 1024,
                height: 1024,
                unit_type: "px",
              },
          template_code: templateInfo?.template_code ?? template_code,
          customizations,
          page_number,
        });
      } catch (error) {
        console.log(error);
        return null;
      }

      if (!project_id) return null;

      let preview_url = "/static/placeholder.png";
      if (template_code) {
        const dimensions =
          image_type === "banner" ? { width: 600, height: 200 } : { width: 800, height: 800 };
        let generatedPreviewUrl: string;
        try {
          generatedPreviewUrl = await getDesignHuddlePreviewUrl({
            template_code,
            customizations,
            page_number,
            ...dimensions,
          });
        } catch (error) {
          console.log(error);
          return null;
        }

        if (!generatedPreviewUrl) return null;
        preview_url = generatedPreviewUrl;
      }

      try {
        await copyImage(getToken, preview_url, project_id);
      } catch (error) {
        console.error(error);
        return null;
      }

      return {
        project_id,
        preview_url: imageURL(user["http://changeengine.com/account_id"], project_id),
      };
    },
    [user, image_type],
  );
}
